/**
 * @typedef {Object} Bundle
 * @property {number} proposal_type_option_id - ID of the proposal type option.
 * @property {number} bundle_product_id - ID of the bundle product.
 * @property {string} bundle_code - Code of the bundle.
 * @property {string} bundle_product_name - Name of the bundle product.
 * @property {string} proposal_type_option_name - Name of the proposal type option.
 * @property {number} proposal_type_option_column - Column index for the proposal option.
 * @property {?number} bundle_product_price - Price of the bundle product (nullable).
 * @property {AhriData} ahri_data - AHRI certification and performance data.
 * @property {Product[]} products_list - List of products included in the bundle.
 */

/**
 * @typedef {Object} AhriData
 * @property {boolean} ahri_match - Whether the product matches AHRI certification.
 * @property {boolean} m1_ahri_match - Whether M1 AHRI match is valid.
 * @property {number} ahri_certificate - AHRI certificate number.
 * @property {?number} ahri_certificate_ac - AC certificate (nullable).
 * @property {?number} ahri_certificate_hp - Heat pump certificate (nullable).
 * @property {?number} ahri_certificate_blr - Boiler certificate (nullable).
 * @property {?number} ahri_certificate_fr - Furnace certificate (nullable).
 * @property {number} cooling_capacity - Cooling capacity in BTUs.
 * @property {number} seer - Seasonal Energy Efficiency Ratio.
 * @property {number} eer - Energy Efficiency Ratio.
 * @property {?number} hspf - Heating Seasonal Performance Factor (nullable).
 * @property {number} seer2 - SEER2 rating.
 * @property {number} eer2 - EER2 rating.
 * @property {?number} hspf2 - HSPF2 rating (nullable).
 * @property {?number} afue - Annual Fuel Utilization Efficiency (nullable).
 */

/**
 * @typedef {Object} Product
 * @property {string} category_name - Name of the category.
 * @property {number} category_id - Category ID.
 * @property {number} parent_category_id - Parent category ID.
 * @property {string} category_type - Type of the category.
 * @property {string} unit_type - Unit type identifier.
 * @property {number} template_component_id - Template component ID.
 * @property {string} product_number - Product number.
 * @property {string} product_id - Product ID.
 * @property {Dimensions} dimensions - Dimensions of the product.
 * @property {?string|?number} product_availability - Product availability status.
 * @property {number} has_replacement - Whether the product has replacement (1 or 0).
 * @property {number} component_type_id - Component type ID.
 * @property {string} component_type_name - Component type name.
 * @property {number} brand_id - Brand ID.
 * @property {string} series - Series name.
 * @property {string} source - Source of the product.
 * @property {?number} retail_cost - Retail cost (nullable).
 * @property {number} capacity - Product capacity.
 * @property {boolean} is_main_component - Whether the product is the main component.
 * @property {string} business_unit_id - Business unit identifier.
 */

/**
 * @typedef {Object} Dimensions
 * @property {string} length - Length in inches.
 * @property {string} width - Width in inches.
 * @property {string} height - Height in inches.
 */

const random = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const tags = [
	{label: 'Air Ninjas Premium', key: 'tier:premium', value: 'tier_premium'},
	{label: 'Tax Credit Eligible', key: 'tax_credit:eligible', value: 'tax_elegible'},
	{label: 'Summer Promo', key: 'promo:summer', value: 'promo_summer_2026'},
	{label: 'Air Ninjas Budget', key: 'tier:budget', value: 'tier_budget'},
	{label: 'Springfield Rebate 2025', key: 'tax_credit:2025', value: 'tax_2025'},
];
const tagSets = {
	premium: [tags[0], tags[1], tags[2]],
	budget: [tags[3], tags[4]],
};

const ahriTemplates = {
	standard: {
		ahri_match: true,
		m1_ahri_match: true,
		cooling_capacity: 23000,
		seer: 13,
		eer: 11,
		hspf: null,
		seer2: 12.6,
		eer2: 10.7,
		hspf2: null,
		afue: null,
	},
	premium: {
		ahri_match: true,
		m1_ahri_match: false,
		cooling_capacity: 18000,
		seer: 16,
		eer: 13,
		hspf: null,
		seer2: 0,
		eer2: 0,
		hspf2: null,
		afue: null,
	},
};

const productTemplates = {
	condenser: {
		category_name: 'Condenser',
		category_id: 1423187165528,
		parent_category_id: 1423187165527,
		category_type: 'E',
		unit_type: 'O',
		component_type_id: 1,
		component_type_name: 'AC',
		has_replacement: 1,
		is_main_component: true,
		source: 'C',
		retail_cost: null,
		capacity: 3,
		dimensions: {length: '25.7500', width: '25.7500', height: '28.6875'},
	},
	fanCoil: {
		category_name: 'Fan Coil',
		category_id: 1423187165537,
		parent_category_id: 1423187165536,
		category_type: 'E',
		unit_type: 'I',
		component_type_id: 2,
		component_type_name: 'Air Handlers',
		has_replacement: 1,
		is_main_component: false,
		source: 'C',
		retail_cost: null,
		capacity: 3,
		dimensions: {length: '16.0000', width: '21.5000', height: '38.0000'},
	},
	furnace: {
		category_name: 'Furnace',
		category_id: 1401460399381,
		parent_category_id: 1401460399380,
		category_type: 'E',
		unit_type: 'O',
		component_type_id: null,
		component_type_name: null,
		has_replacement: 1,
		is_main_component: false,
		source: 'C',
		retail_cost: null,
		capacity: null,
		dimensions: {length: '24.0000', width: '17.5000', height: '29.0000'},
	},
};

const productsAlternate = [
	{
		condenser: 'OCA1636B003',
		condenserAlt: 'OCA2036B003',
		fanCoil: 'NVNV371721',
		fanCoilAlt: null,
		furnace: 'YRS8090114',
		furnaceAlt: null,
		tags: [tags[3], tags[4]],
		tagsAlt: [tags[0], tags[1], tags[2]],
	},
	{
		condenser: 'OCA2036B003',
		condenserAlt: null,
		fanCoil: 'NVNV371721',
		fanCoilAlt: 'NHNH361724',
		furnace: 'VRA8090112',
		furnaceAlt: null,
		tags: [tags[3], tags[4]],
		tagsAlt: [tags[3]],
	},
	{
		condenser: 'OCA1436A003',
		condenserAlt: null,
		fanCoil: 'NHNH361724',
		fanCoilAlt: null,
		furnace: 'TPC8080112',
		furnaceAlt: 'VRA8090112',
		tags: [tags[0], tags[1], tags[2]],
		tagsAlt: [tags[0], tags[4]],
	},
];

function inRange(index, min, max) {
	return index >= min && index <= max;
}

const brands = ['OCA', 'Bryant', 'Carrier', 'Tempstar'];
const series = ['148TAN', '146SAN', '135SAN', '26VNA', '26SPA', '26TPA', '26SCA'];

function generateBundle(index) {
	const id = 201 + index;
	const brandName = brands[index % brands.length];
	const seriesName = series[index % series.length];
	const tier = index % 3 === 0 ? 'premium' : 'budget';

	return {
		proposal_type_option_id: id,
		bundle_product_id: 501 + index,
		bundle_product_name: `${brandName} ${seriesName} AC 3 Ton`,
		bundle_product_price: null,
		tags: inRange(index, 0, 2) ? productsAlternate[index].tags : tagSets[tier],
		ahri_data: {
			...ahriTemplates[tier === 'premium' ? 'premium' : 'standard'],
			ahri_certificate: random(9000000, 12999999),
			ahri_certificate_ac: null,
			ahri_certificate_hp: null,
			ahri_certificate_blr: null,
			ahri_certificate_fr: null,
		},
		products_list: [
			{
				...productTemplates.condenser,
				template_component_id: 81822 + index,
				product_number: inRange(index, 0, 2)
					? productsAlternate[index].condenser
					: `${seriesName}${random(100, 999)}W003`,
				product_id: index % 2 === 0 ? '' : `139168${random(100000, 999999)}`,
				product_availability: null,
				brand_id: random(1, 131),
				series: seriesName,
				product_cost: random(380, 500),
				business_unit_id: index % 3 === 0 ? 'BK' : 'NW',
			},
			{
				...productTemplates.fanCoil,
				template_component_id: 81823 + index,
				product_number: inRange(index, 0, 2)
					? productsAlternate[index].fanCoil
					: `${['NVNV', 'NHNH', 'CVAMA', 'CAAMP'][index % 4]}${random(100000, 999999)}`,
				product_id: index % 2 === 0 ? '' : `146174${random(100000, 999999)}`,
				product_availability: null,
				brand_id: random(129, 703),
				series: ['FE4A', 'FB4C', 'FCM4-CMK', 'FMA4P-CMK'][index % 4],
				product_cost: random(280, 380),
				business_unit_id: index % 3 === 0 ? 'BK' : 'NW',
			},
			{
				...productTemplates.furnace,
				template_component_id: 85914 + index,
				product_number: inRange(index, 0, 2)
					? productsAlternate[index].furnace
					: `${['TPC', 'YRS', 'VRA', '59MN'][index % 4]}${random(1000000, 9999999)}`,
				product_id: index % 2 === 0 ? '' : `50${random(1000, 9999)}`,
				product_availability: null,
				brand_id: null,
				product_cost: random(180, 300),
				business_unit_id: index % 3 === 0 ? 'CE' : 'NW',
			},
		],
	};
}

/** @type {Bundle[]} */
export const bundlesMock = Array.from({length: 16}, (_, i) => generateBundle(i));
export const bundlesForTestMock = Array.from({length: 3}, (_, i) => generateBundle(i));

/**
 * @typedef {Object} Price
 * @property {string} universal_unique_id - UUID of the pricing record.
 * @property {number} bundle_equipment_tax_paid_value - Tax paid value for bundle equipment.
 * @property {number} bundle_adjustment_buffer_value - Adjustment buffer value for bundle.
 * @property {number} bundle_product_price - Total bundle product price.
 * @property {PriceProductList[]} products_list - List of products in the pricing bundle.
 */

/**
 * @typedef {Object} PriceProductList
 * @property {string} product_number - Product number identifier.
 * @property {number} category_id - Category ID of the product.
 * @property {string} business_unit_id - Business unit identifier (e.g., "CE").
 * @property {number} quantity - Quantity of the product.
 * @property {number} product_availability - Availability status (0 = unavailable, 1 = available).
 * @property {number} retail_cost - Retail cost of the product.
 */

/** @type {Price[]} */
export const pricesMock = [
	{
		universal_unique_id: 'c93d898f-7882-ef81-18c8-71523cd61578',
		bundle_equipment_tax_paid_value: 0,
		bundle_adjustment_buffer_value: 0,
		bundle_product_price: 7342,
		products_list: [
			{
				product_number: '116BNA018000',
				category_id: 1423187165528,
				business_unit_id: 'CE',
				quantity: 1,
				product_availability: 0,
				retail_cost: 2265.71,
			},
			{
				product_number: 'FE4ANF003L00',
				category_id: 1423187165537,
				business_unit_id: 'CE',
				quantity: 1,
				product_availability: 0,
				retail_cost: 3334.29,
			},
			{
				product_number: 'TC-NAC01-A',
				category_id: 1401460399381,
				business_unit_id: 'CE',
				quantity: 1,
				product_availability: 0,
				retail_cost: 1442,
			},
		],
	},
];

/**
 * @typedef {Object} Tag
 * @property {string} label - Tag readable label.
 * @property {string} key - Tag unique system key name.
 * @property {string} value - Tag value.
 */

/** @type {Tag[]} */
export const bundleTagsMock = [
	{label: 'Furnace 80%', key: 'furnace:80', value: 'furnace_80'},
	{label: 'Furnace 90%', key: 'furnace:90', value: 'furnace_90'},
	{label: 'Upflow Coil', key: 'upflow:coil', value: 'coil_upflow'},
	{label: 'Horizontal Coil', key: 'coil:horizontal', value: 'coil_horizontal'},
	{label: 'Multiposition Coil', key: 'coil:multiposition', value: 'coil_multiposition'},
	{label: 'Tax Credit Eligible', key: 'tax:eligible', value: 'tax_elegible'},
	{label: 'Springfield Rebate 2025', key: 'tax:2025', value: 'tax_2025'},
	{label: 'Summer Promo', key: 'promo:summer', value: 'promo_summer_2026'},
	{label: 'Air Ninjas Premium', key: 'tier:premium', value: 'tier_premium'},
	{label: 'Air Ninjas Budget', key: 'tier:budget', value: 'tier_budget'},
];

/**
 * @typedef {Object} Accessory
 * @property {string} product_id - Accessory id.
 * @property {string} product_name - Accessory product name.
 * @property {string} model_number - Accessory model number.
 * @property {number} product_cost - Accessory product cost.
 * @property {number} category_id - Accessory category id.
 * @property {string} business_unit_id - Accessory business unit id.
 */

/** @type {Accessory[]} */
export const accessoriesData = [
	{
		product_id: '507415',
		product_name: 'ABC Thermostat',
		model_number: 'CUSTOM-14GE4S',
		product_cost: 100,
		category_id: 1401460399381,
		business_unit_id: 'NW',
	},
	{
		product_id: '1410348558540',
		product_name: 'CARRIER CORP CARRIER BRAND 4 Tons 14-19 SEER Air Conditioner 208/230 V 5 Stage R-410A',
		model_number: '24VNA948A003',
		product_cost: 4304,
		category_id: 1401460399382,
		business_unit_id: 'CE',
	},
	{
		product_id: '1434960975861',
		product_name: 'CarrierÂ® Comfortâ¢ - TC-NAC01-A  Non Programmable AC Thermostat',
		model_number: 'TC-NAC01-A',
		product_cost: 144.2,
		category_id: 1401460399381,
		business_unit_id: 'CE',
	},
	{
		product_id: '1429461218337',
		product_name:
			'CarrierÂ® Infinityâ¢ - 1 Ton 19 SEER Residential Variable Speed Air Conditioner Condensing Unit',
		model_number: '24VNA913A003',
		product_cost: 3683,
		category_id: 1401460399400,
		business_unit_id: 'CE',
	},
	{
		product_id: '507421',
		product_name: 'Custom Job',
		model_number: 'CUSTOM-EAVR06',
		product_cost: 450,
		category_id: 1401460399384,
		business_unit_id: 'NW',
	},
	{
		product_id: '1474878715943',
		product_name: 'Factory Authorized Partsâ¢ - TC-NAC01-A Non-Programmable AC Thermostat',
		model_number: 'TC-NAC01-A',
		product_cost: 144.2,
		category_id: 1401460399381,
		business_unit_id: 'CE',
	},
	{
		product_id: '507417',
		product_name: 'Premier Warranty',
		model_number: 'CUSTOM-R2C647',
		product_cost: 0,
		category_id: 1401460399381,
		business_unit_id: 'NW',
	},
	{
		product_id: '1433924142614',
		product_name: 'Rags Unlimited - GVNPFXL - Disposable Glove Xl NitrIle',
		model_number: 'GVNPFXL',
		product_cost: 0,
		category_id: 1401460399383,
		business_unit_id: 'CE',
	},
	{
		product_id: '507416',
		product_name: 'XYZ Heat Kit',
		model_number: 'CUSTOM-KPFPPQ',
		product_cost: 50,
		category_id: 1401460399381,
		business_unit_id: 'NW',
	},
];

export const brandsData = [
	{
		brand_id: 117,
		brand_name: 'Advanced Distributor Products',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/advanced_distributor_products.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 6,
		brand_name: 'American Standard',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/american_standard.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 2,
		brand_name: 'Bryant',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/bryant.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 1,
		brand_name: 'Carrier',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/carrier.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 123,
		brand_name: 'GREE',
		brand_logo: null,
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 703,
		brand_name: 'Multi',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/multi.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 6,
	},
	{
		brand_id: 2002,
		brand_name: 'Nortek',
		brand_logo: null,
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 3,
		brand_name: 'Payne',
		brand_logo: 'https://engage-brand-logos.s3.amazonaws.com/payne.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 4,
		brand_name: 'Rheem',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/rheem.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
	{
		brand_id: 131,
		brand_name: 'Tempstar',
		brand_logo: 'https://s3.amazonaws.com/engage-brand-logos/tempstar.svg',
		brand_url: null,
		is_custom: false,
		count_selected_series: 0,
	},
];

const IMG =
	'https://resource.carrierenterprise.com/is/image/Watscocom/_fb4cnf018l00_article_1391689353958_en_normal?wid=350&hei=350&defaultImage=Eng_coming_soon';
const seriesLabels = ['Condenser', 'Furnace', 'Fan Coil'];
export const seriesData = seriesLabels.flatMap((label, i) =>
	Array.from(Array('OCA2', 'NHNH', 'TPC', 'VRA8'), (s, j) => ({
		series_id: 660 + i * 4 + j, // unique across all items
		series: s,
		key_attributes: [
			{
				key: 'BlowerTech',
				value: 'Variable-speed',
			},
		],
		label,
		image_source: IMG,
		alternates_count: 2,
	}))
);

export const productsData = seriesData.flatMap((s, i) =>
	Array.from(Array(2), (_, j) => ({
		product_id: 139168935234 + i * 4 + j, // unique across all items
		product_number: `${s.series}${random(1000) + i}`,
		product_name: '3 Ton Residential Fan Coil',
		series_id: s.series_id,
		series: s.series,
		dimensions: {
			length: '22.0625',
			width: '17.6250',
			height: '42.6875',
		},
		product_is_selected: false,
		phase: null,
		voltage: null,
		seer: null,
		afue: null,
		stages: null,
		tonnage: '3.0000',
		air_flow: null,
		hspf: null,
		ahri_data: {
			ahri_match: true,
			m1_ahri_match: false,
			ahri_certificate: 9243565,
			ahri_certificate_ac: null,
			ahri_certificate_hp: null,
			ahri_certificate_blr: null,
			ahri_certificate_fr: null,
			cooling_capacity: 18000,
			seer: 16,
			eer: 13,
			hspf: null,
			seer2: 0,
			eer2: 0,
			hspf2: null,
			afue: null,
		},
		tags: [...bundleTagsMock.slice(-1)],
		product_cost: null,
		net_availability: null,
		price_last_update: null,
	}))
);
